import io from "socket.io-client";
import SearchUtils from "../utils/SearchUtils";
import DateUtils from "../utils/DateUtils";

/**
 * Socket service for managing Socket.io connection and events
 * Requires component reference to access state and methods
 */
class SocketService {
  constructor(component) {
    this.component = component;
    this.socket = null;
  }

  /**
   * Initialize socket connection and setup event handlers
   */
  init() {
    const user = sessionStorage.getItem("user");
    if (!this.socket && user) {
      this.socket = io(
        `${process.env.REACT_APP_VIDEO_SOCKET_BASEURL}/console-socket`,
        {
          transports: ["websocket"],
          auth: {
            token: `Bearer ${JSON.parse(user).signInUserSession.idToken.jwtToken}`,
          },
          secure: true,
        }
      );

      // Update component's socket reference
      this.component.socket = this.socket;

      // Socket connection events
      this.socket &&
        this.socket.on("connect", () => {
          console.log("Socket connection established from CounterAssignUI");
        });
      this.socket &&
        this.socket.on("connect_error", (error) => {
          console.log("Socket Connect Error: ", error);
        });
      this.socket &&
        this.socket.on("disconnect", (reason) => {
          console.log(`Socket Disconnected: ${reason}`);
        });

      // Console event - receives all delivery types
      this.socket &&
        this.socket.on("console", async (res) => {
          try {
            this.component.setState({
              counterOrders: [],
              lastConsoleSnapshot: res,
            });

            // Store search text before state update
            const currentSearchText = this.component.state.searchText;

            const activeConsoleKey = this.component.getActiveConsoleKey
              ? this.component.getActiveConsoleKey()
              : "counter";
            const counterData = res && res[activeConsoleKey];
            // Use websocket data only for Pay Later; no fallback to API or previous state
            const results = (counterData && counterData.results) || [];
            const total = (counterData && counterData.total) || 0;
            const resultCount = results.length;

            const pendingShortcut = this.component.state.pendingCounterShortcutSearch;
            const isCounterShortcutFallback = pendingShortcut && pendingShortcut.pucode;

            this.component.setState(
              {
                loadingCounterOrders: false,
                loadingMoreCounter: false,
                counterOrders: results,
                counterDataTotal: total,
                hasMoreCounter: resultCount > 0 && resultCount < total,
                rowPerPageCounter: resultCount >= 10 ? resultCount : 10,
                ...(isCounterShortcutFallback ? { pendingCounterShortcutSearch: null } : {}),
              },
              () => {
                if (isCounterShortcutFallback) {
                  const searchPuCode = pendingShortcut.pucode.toLowerCase();
                  const matchedOrder = results.find(
                    (order) =>
                      order && order.pucode && order.pucode.toLowerCase() === searchPuCode
                  );

                  if (matchedOrder) {
                    const nextState = {
                      loader: true,
                      ...(pendingShortcut.source === "search" ? { searchText: "" } : {}),
                    };

                    this.component.props.onToggleLoader && this.component.props.onToggleLoader(true);
                    this.component.setState(nextState, () => {
                      if (pendingShortcut.source === "scan") {
                        this.component.props.onRxScan && this.component.props.onRxScan("");
                      }
                      this.component.handleOpen(activeConsoleKey, matchedOrder, "qr_scan");
                    });
                    return;
                  } else {
                    this.component.props.onToggleLoader && this.component.props.onToggleLoader(false);
                    return;
                  }
                }

                // Skip auto-open when strict patient verification is enabled
                if (this.component.state.strictPatientVerification) {
                  return;
                }

                const trimmedSearchText = (currentSearchText || "").trim();
                const normalizedSearchText = trimmedSearchText.toLowerCase();

                if (
                  trimmedSearchText.length > 1 &&
                  normalizedSearchText !== this.component.state.lastSearchText &&
                  !this.component.state.open
                ) {
                  const collections = [
                    { data: res[activeConsoleKey], type: activeConsoleKey },
                  ];

                  const matches = SearchUtils.findOrdersMatchingSearch(
                    trimmedSearchText,
                    collections,
                    DateUtils.normalizeDobValue
                  );

                  if (matches.length === 1) {
                    const matchedOrder = matches[0];
                    this.component.setState({ lastSearchText: normalizedSearchText });
                    this.component.handleOpen(matchedOrder.deliveryType, matchedOrder);
                  }
                }
              }
            );
          } catch (error) {
            console.error("Error handling console event:", error);
            const hadPendingShortcut = this.component.state.pendingCounterShortcutSearch;
            this.component.setState({
              loadingCounterOrders: false,
              loadingMoreCounter: false,
              pendingCounterShortcutSearch: null,
            });
            if (hadPendingShortcut) {
              this.component.props.onToggleLoader && this.component.props.onToggleLoader(false);
            }
          }
        });

      // Counter-specific event (for pagination updates)
      this.socket &&
        this.socket.on("counter", async (res) => {
          try {
            const results = (res && res.results) || [];
            const total = (res && res.total) || 0;
            const resultCount = results.length;

            const pendingShortcut = this.component.state.pendingCounterShortcutSearch;
            const isCounterShortcutFallback = pendingShortcut && pendingShortcut.pucode;

            this.component.setState(
              {
                loadingCounterOrders: false,
                loadingMoreCounter: false,
                counterOrders: results,
                lastConsoleSnapshot: {
                  ...(this.component.state.lastConsoleSnapshot || {}),
                  counter: res,
                },
                counterDataTotal: total,
                hasMoreCounter: resultCount > 0 && resultCount < total,
                rowPerPageCounter: resultCount >= 10 ? resultCount : 10,
                ...(isCounterShortcutFallback ? { pendingCounterShortcutSearch: null } : {}),
              },
              () => {
                if (isCounterShortcutFallback) {
                  const searchPuCode = pendingShortcut.pucode.toLowerCase();
                  const matchedOrder = results.find(
                    (order) =>
                      order && order.pucode && order.pucode.toLowerCase() === searchPuCode
                  );

                  if (matchedOrder) {
                    const nextState = {
                      loader: true,
                      ...(pendingShortcut.source === "search" ? { searchText: "" } : {}),
                    };

                    this.component.props.onToggleLoader && this.component.props.onToggleLoader(true);
                    this.component.setState(nextState, () => {
                      if (pendingShortcut.source === "scan") {
                        this.component.props.onRxScan && this.component.props.onRxScan("");
                      }
                      this.component.handleOpen(
                        pendingShortcut.consoleKey || "counter",
                        matchedOrder,
                        "qr_scan"
                      );
                    });
                    return;
                  } else {
                    this.component.props.onToggleLoader && this.component.props.onToggleLoader(false);
                    return;
                  }
                }

                // Skip auto-open when strict patient verification is enabled
                if (this.component.state.strictPatientVerification) {
                  return;
                }

                const currentSearchValue = this.component.state.searchText;
                const trimmedSearchText = (currentSearchValue || "").trim();
                const normalizedSearchText = trimmedSearchText.toLowerCase();

                if (
                  trimmedSearchText.length > 1 &&
                  normalizedSearchText !== this.component.state.lastSearchText &&
                  !this.component.state.open
                ) {
                  const collections = [{ data: res, type: "counter" }];
                  const matches = SearchUtils.findOrdersMatchingSearch(
                    trimmedSearchText,
                    collections,
                    DateUtils.normalizeDobValue
                  );

                  if (matches.length === 1) {
                    const matchedOrder = matches[0];
                    this.component.setState({ lastSearchText: normalizedSearchText });
                    this.component.handleOpen(matchedOrder.deliveryType, matchedOrder);
                  }
                }
              }
            );
          } catch (error) {
            console.error("Error handling counter event:", error);
            const hadPendingShortcut = this.component.state.pendingCounterShortcutSearch;
            this.component.setState({
              loadingCounterOrders: false,
              loadingMoreCounter: false,
              pendingCounterShortcutSearch: null,
            });
            if (hadPendingShortcut) {
              this.component.props.onToggleLoader && this.component.props.onToggleLoader(false);
            }
          }
        });

      // Counter order arrived - play sound (emit from websocket when order status becomes Arrived; payload: { order_id })
      this.socket &&
        this.socket.on("counterOrderArrived", (payload) => {
          this.component.playArrivalSound && this.component.playArrivalSound();
        });

      // Console changes event - This is the key event that should fire after order creation
      this.socket &&
        this.socket.on("changesOnConsole", () => {
          if (this.component.consoleRequest && this.socket) {
            this.socket.emit("getConsole", this.component.consoleRequest);
          }
        });

      // Consultation event handlers
      this.setupConsultationEventHandlers();
    }
  }

  /**
   * Setup consultation-related socket event handlers
   */
  setupConsultationEventHandlers() {
    if (!this.socket) return;

    // Call accepted by pharmacist
    this.socket.on("callAccepted", (payload) => {
      console.log("Call accepted:", payload);
      if (this.component.setState) {
        if (payload && payload.isAudioOnlyConsult) {
          this.component.setState({ isAudioOnlyConsult: payload.isAudioOnlyConsult });
        }
        if (payload && payload.connectedPharmacistName) {
          this.component.setState({ connectedPharmacistName: payload.connectedPharmacistName });
        }
        // Update status to accepted
        if (this.component.consultationService && this.component.state.consultUserId) {
          this.component.consultationService.updateConsultationStatus(
            this.component.state.consultUserId,
            "Accepted"
          );
        }
        // Clear timeout
        if (this.component.consultationService && this.component.consultationService.consultTimeoutRef) {
          clearTimeout(this.component.consultationService.consultTimeoutRef);
        }
        this.component.setState({
          isConsultAcceptedCall: true,
          startRinging: false,
        });
      }
    });

    // Consultation completed from portal
    this.socket.on("consultCompleteFromPortal", (payload) => {
      console.log("Consultation completed from portal:", payload);
      if (this.component.setState) {
        this.component.setState({ startRinging: false });
      }
      if (this.component.consultationService) {
        this.component.consultationService.endVideoConsultation(true, false);
      }
    });

    // Order released during consultation
    this.socket.on("orderReleased", (payload) => {
      console.log("Order released:", payload);
      if (this.component.setState) {
        this.component.setState({
          isDisable: false,
          startRinging: false,
          isReleasedModal: true,
        });
      }
    });

    // Session ended
    this.socket.on("endSession", (payload) => {
      console.log("Session ended:", payload);
      if (this.component.consultationService && this.component.consultationService.consultTimeoutRef) {
        clearTimeout(this.component.consultationService.consultTimeoutRef);
      }
      if (this.component.setState) {
        this.component.setState({ startRinging: false });
      }
    });

    // Call declined
    this.socket.on("callDeclined", (payload) => {
      console.log("Call declined:", payload);
      if (this.component.setState) {
        this.component.setState({ startRinging: false });
      }
      if (this.component.consultationService) {
        this.component.consultationService.cleanup();
      }
    });

    // Order released from delivery/prescription
    this.socket.on("isOrderReleasedFromDeliveryPrescription", (payload) => {
      console.log("Order released from delivery/prescription:", payload);
      if (this.component.setState) {
        this.component.setState({
          isDisable: false,
          isReleasedModal: true,
          startRinging: false,
        });
      }
    });

    // Box ID configured
    this.socket.on("BoxIdConfigured", (payload) => {
      console.log("Box ID configured:", payload);
    });
  }

  /**
   * Cleanup socket connection and remove event listeners
   */
  cleanup() {
    if (this.socket) {
      this.socket.off("connect");
      this.socket.off("connect_error");
      this.socket.off("disconnect");
      this.socket.off("console");
      this.socket.off("counter");
      this.socket.off("counterOrderArrived");
      this.socket.off("changesOnConsole");
      // Cleanup consultation event handlers
      this.socket.off("callAccepted");
      this.socket.off("consultCompleteFromPortal");
      this.socket.off("orderReleased");
      this.socket.off("endSession");
      this.socket.off("callDeclined");
      this.socket.off("isOrderReleasedFromDeliveryPrescription");
      this.socket.off("BoxIdConfigured");
      this.socket.disconnect();
      this.socket = null;
    }
  }

  /**
   * Get socket instance
   */
  getSocket() {
    return this.socket;
  }

  /**
   * Emit socket event
   */
  emit(event, data) {
    if (this.socket) {
      this.socket.emit(event, data);
    }
  }
}

export default SocketService;



// WEBPACK FOOTER //
// ./src/components/assign-prescriptions/counter/services/SocketService.js